// src/pages/CanvasEditor.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Importando componentes do MUI
import { 
    Container, 
    Paper, 
    TextField, 
    Typography, 
    Button, 
    Box, 
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';

// Componente CanvasBlock (Sem alterações)
const CanvasBlock = ({ title, content, onChange, fieldName }) => {
    return (
        <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ borderBottom: '1px solid #ddd', pb: 1, fontSize: '1rem', fontWeight: 'bold' }}>
                {title}
            </Typography>
            <TextField
                value={content || ''}
                onChange={(e) => onChange(fieldName, e.target.value)}
                multiline
                rows={8}
                variant="outlined"
                fullWidth
                sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' } }}
                placeholder={`Adicione ${title.toLowerCase()}...`}
            />
        </Paper>
    );
};


const CanvasEditor = () => {
    const { id } = useParams();
    const [canvas, setCanvas] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // fetchCanvas (Sem alterações)
    const fetchCanvas = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(`/models/${id}`);
            setCanvas(response.data);
        } catch (err) {
            setError('Não foi possível carregar os dados do canvas.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchCanvas();
    }, [fetchCanvas]);

    // handleChange (Sem alterações)
    const handleChange = (fieldName, value) => {
        setCanvas(prevCanvas => ({ ...prevCanvas, [fieldName]: value }));
    };

    // ==========================================================
    // CORREÇÃO FINAL NA FUNÇÃO handleSave
    // ==========================================================
    const handleSave = async () => {
        setIsSaving(true);

        // 1. Criamos um objeto "limpo" (payload)
        const updatedData = {
            // 2. Adicionamos os 9 campos do canvas
            keyPartners: canvas.keyPartners,
            keyActivities: canvas.keyActivities,
            keyResources: canvas.keyResources,
            valuePropositions: canvas.valuePropositions,
            customerRelationships: canvas.customerRelationships,
            channels: canvas.channels,
            customerSegments: canvas.customerSegments,
            costStructure: canvas.costStructure,
            revenueStreams: canvas.revenueStreams,
            
            // 3. A CORREÇÃO: Adicionamos o 'title'
            // O backend espera 'title', mas o estado 'canvas' tem 'name'
            title: canvas.name 
        };

        try {
            // 4. Enviamos o payload correto
            await api.put(`/models/${id}`, updatedData);
            setSnackbar({ open: true, message: 'Canvas salvo com sucesso!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao salvar. Tente novamente.', severity: 'error' });
            console.error(err); 
        } finally {
            setIsSaving(false);
        }
    };
    // ==========================================================
    // FIM DA CORREÇÃO
    // ==========================================================


    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // handleGeneratePdf (Sem alterações)
    const handleGeneratePdf = () => {
        setIsGeneratingPdf(true);
        const canvasContainer = document.getElementById('canvas-container');

        html2canvas(canvasContainer, { 
            scale: 2,
            logging: false,
            useCORS: true,
        }).then((canvasScreenshot) => {
            const imgData = canvasScreenshot.toDataURL('image/png');
            
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 40;

            pdf.setFontSize(22);
            // CORREÇÃO AQUI TAMBÉM: Usar canvas.name, pois foi assim que carregamos
            pdf.text(canvas.name || 'Business Model Canvas', pdfWidth / 2, margin, { align: 'center' });

            const imgProps = pdf.getImageProperties(imgData);
            const aspectRatio = imgProps.height / imgProps.width;
            const imgWidth = pdfWidth - margin * 2;
            const imgHeight = imgWidth * aspectRatio;

            pdf.addImage(imgData, 'PNG', margin, margin + 20, imgWidth, imgHeight);

            pdf.setFontSize(10);
            const dateStr = `Relatório gerado em: ${new Date().toLocaleDateString('pt-BR')}`;
            pdf.text(dateStr, margin, pdfHeight - margin / 2);

            pdf.save(`canvas-business-model-${id}.pdf`);
        }).finally(() => {
            setIsGeneratingPdf(false);
        });
    };

    // Renderização (Sem alterações)
    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;
    }

    if (error) {
        return <Typography color="error" sx={{ p: 4 }}>Erro: {error}</Typography>;
    }

    if (!canvas) {
        return <Typography sx={{ p: 4 }}>Canvas não encontrado.</Typography>;
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Container 
                maxWidth="xl" 
                sx={{ 
                    py: 4,
                    transform: 'scale(0.8)',
                    transformOrigin: 'top center',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <div>
                        <Button component={RouterLink} to="/canvases" startIcon={<ArrowBackIcon />}>
                            Voltar para a Lista
                        </Button>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {canvas.name}
                        </Typography>
                    </div>
                    <Box>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            size="large"
                            startIcon={<SaveIcon />}
                            onClick={handleSave} 
                            disabled={isSaving}
                            sx={{ mr: 2 }}
                        >
                            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="secondary" 
                            size="large" 
                            startIcon={<PdfIcon />} 
                            onClick={handleGeneratePdf} 
                            disabled={isGeneratingPdf}
                        >
                            {isGeneratingPdf ? 'Gerando...' : 'Gerar Relatório'}
                        </Button>
                    </Box>
                </Box>

                <Box id="canvas-container" display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <Box flex={1}><CanvasBlock title="Key Partners" content={canvas.keyPartners} onChange={handleChange} fieldName="keyPartners" /></Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={2}>
                            <Box flex={1}><CanvasBlock title="Key Activities" content={canvas.keyActivities} onChange={handleChange} fieldName="keyActivities" /></Box>
                            <Box flex={1}><CanvasBlock title="Key Resources" content={canvas.keyResources} onChange={handleChange} fieldName="keyResources" /></Box>
                        </Box>
                        <Box flex={1}><CanvasBlock title="Value Proposition" content={canvas.valuePropositions} onChange={handleChange} fieldName="valuePropositions" /></Box>
                        <Box flex={1} display="flex" flexDirection="column" gap={2}>
                            <Box flex={1}><CanvasBlock title="Customer Relationship" content={canvas.customerRelationships} onChange={handleChange} fieldName="customerRelationships" /></Box>
                            <Box flex={1}><CanvasBlock title="Channels" content={canvas.channels} onChange={handleChange} fieldName="channels" /></Box>
                        </Box>
                        <Box flex={1}><CanvasBlock title="Customer Segments" content={canvas.customerSegments} onChange={handleChange} fieldName="customerSegments" /></Box>
                    </Box>
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        <Box flex={1}><CanvasBlock title="Cost Structure" content={canvas.costStructure} onChange={handleChange} fieldName="costStructure" /></Box>
                        <Box flex={1}><CanvasBlock title="Revenue Streams" content={canvas.revenueStreams} onChange={handleChange} fieldName="revenueStreams" /></Box>
                    </Box>
                </Box>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default CanvasEditor;